import { Field, NumberInput, TextInput } from "@/components/base/FormControls";
import { money } from "@/lib/format";
import type { Currency } from "@/lib/types";

export type MarginMode = "percent" | "fixed";

export interface ItemDraft {
  key: string;
  name: string;
  category: string;
  quantity: string;
  unitCost: string;
  salePrice: string;
  marginMode: MarginMode;
  marginValue: string;
  expirationDate: string;
}

interface Props {
  index: number;
  draft: ItemDraft;
  currency: Currency;
  suggestions: string[];
  onChange: (patch: Partial<ItemDraft>) => void;
  onRemove: () => void;
  onPickSuggestion: (name: string) => void;
  canRemove: boolean;
}

function parse(value: string): number {
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : 0;
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

function calcSalePrice(cost: number, mode: MarginMode, value: number): number {
  if (mode === "percent") return round2(cost * (1 + value / 100));
  return round2(cost + value);
}

export default function PurchaseItemCard({
  index,
  draft,
  currency,
  suggestions,
  onChange,
  onRemove,
  onPickSuggestion,
  canRemove,
}: Props) {
  const subtotal = parse(draft.quantity) * parse(draft.unitCost);
  const revenue = parse(draft.quantity) * parse(draft.salePrice);
  const marginAmount = parse(draft.salePrice) - parse(draft.unitCost);
  const marginPercent =
    parse(draft.unitCost) > 0 ? (marginAmount / parse(draft.unitCost)) * 100 : 0;

  const moneyPrefix = currency === "USD" ? "US$" : "RD$";

  const apply = (next: { unitCost?: string; marginMode?: MarginMode; marginValue?: string }) => {
    const costRaw = next.unitCost ?? draft.unitCost;
    const mode = next.marginMode ?? draft.marginMode;
    const valueRaw = next.marginValue ?? draft.marginValue;
    const patch: Partial<ItemDraft> = { ...next };

    // Solo calculamos el precio cuando hay un margen definido.
    // Si el margen está vacío, dejamos que el precio de venta se escriba a mano.
    if (valueRaw.trim() !== "") {
      const price = calcSalePrice(parse(costRaw), mode, parse(valueRaw));
      patch.salePrice = parse(costRaw) > 0 || parse(valueRaw) > 0 ? String(price) : "";
    }
    onChange(patch);
  };

  return (
    <div className="rounded-3xl border border-background-200/70 bg-background-50 p-4 shadow-card">
      <div className="mb-3 flex items-center justify-between">
        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary-100 text-[12px] font-semibold text-primary-700">
          {index + 1}
        </span>
        {canRemove ? (
          <button
            type="button"
            onClick={onRemove}
            className="flex h-7 w-7 items-center justify-center rounded-full bg-background-200 text-foreground-500"
          >
            <i className="ri-delete-bin-line text-[15px]" />
          </button>
        ) : null}
      </div>

      <div className="space-y-3">
        <Field label="Producto">
          <TextInput
            value={draft.name}
            onChange={(v) => onChange({ name: v })}
            placeholder="Ej. Bocina grande"
            icon="ri-price-tag-3-line"
          />
        </Field>

        <Field label="Categoría (opcional)">
          <TextInput
            value={draft.category}
            onChange={(v) => onChange({ category: v })}
            placeholder="Ej. Audio"
            icon="ri-bookmark-line"
          />
        </Field>

        {suggestions.length > 0 ? (
          <div className="-mt-1 flex flex-wrap gap-1.5">
            {suggestions.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => onPickSuggestion(s)}
                className="rounded-full bg-secondary-100 px-2.5 py-1 text-[11px] font-medium text-secondary-800"
              >
                {s}
              </button>
            ))}
          </div>
        ) : null}

        <div className="grid grid-cols-2 gap-3">
          <Field label="Cantidad">
            <NumberInput
              value={draft.quantity}
              onChange={(v) => onChange({ quantity: v })}
              placeholder="0"
            />
          </Field>
          <Field label="Costo unitario">
            <NumberInput
              value={draft.unitCost}
              onChange={(v) => apply({ unitCost: v })}
              placeholder="0.00"
              prefix={moneyPrefix}
            />
          </Field>
        </div>

        <div className="rounded-2xl bg-background-100 p-3">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-[12px] font-medium text-foreground-600">
              Margen de ganancia (opcional)
            </span>
            <div className="flex rounded-full bg-background-200 p-0.5">
              <button
                type="button"
                onClick={() => apply({ marginMode: "percent" })}
                className={`rounded-full px-3 py-1 text-[11px] font-semibold transition-colors ${
                  draft.marginMode === "percent"
                    ? "bg-background-50 text-primary-700 shadow-sm"
                    : "text-foreground-500"
                }`}
              >
                Porcentaje
              </button>
              <button
                type="button"
                onClick={() => apply({ marginMode: "fixed" })}
                className={`rounded-full px-3 py-1 text-[11px] font-semibold transition-colors ${
                  draft.marginMode === "fixed"
                    ? "bg-background-50 text-primary-700 shadow-sm"
                    : "text-foreground-500"
                }`}
              >
                Monto fijo
              </button>
            </div>
          </div>
          <NumberInput
            value={draft.marginValue}
            onChange={(v) => apply({ marginValue: v })}
            placeholder="0"
            prefix={draft.marginMode === "fixed" ? moneyPrefix : undefined}
            suffix={draft.marginMode === "percent" ? "%" : undefined}
          />
          <p className="mt-1.5 text-[11px] text-foreground-400">
            {draft.marginMode === "percent"
              ? "El precio de venta = costo + el porcentaje que pongas."
              : "El precio de venta = costo + el monto fijo que pongas."}
          </p>
        </div>

        <Field label="Precio de venta unitario">
          <NumberInput
            value={draft.salePrice}
            onChange={(v) => onChange({ salePrice: v, marginValue: "" })}
            placeholder="0.00"
            prefix={moneyPrefix}
          />
        </Field>

        <Field label="Fecha de vencimiento (opcional)">
          <TextInput
            value={draft.expirationDate}
            onChange={(v) => onChange({ expirationDate: v })}
            type="date"
            icon="ri-calendar-event-line"
          />
        </Field>

        <div className="grid grid-cols-2 gap-3 rounded-2xl bg-background-100 p-3">
          <div>
            <p className="text-[10px] text-foreground-500">Subtotal costo</p>
            <p className="tabular-nums text-[14px] font-semibold text-foreground-900">
              {money(subtotal, currency)}
            </p>
          </div>
          <div>
            <p className="text-[10px] text-foreground-500">Ingreso potencial</p>
            <p className="tabular-nums text-[14px] font-semibold text-primary-700">
              {money(revenue, currency)}
            </p>
          </div>
        </div>

        {parse(draft.salePrice) > 0 && parse(draft.unitCost) > 0 ? (
          <div className="flex items-center justify-between rounded-2xl bg-primary-50 px-3 py-2">
            <span className="text-[11px] text-foreground-600">Margen por unidad</span>
            <span className="tabular-nums text-[12px] font-semibold text-primary-700">
              {money(marginAmount, currency)} · {Math.round(marginPercent)}%
            </span>
          </div>
        ) : null}
      </div>
    </div>
  );
}